import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchCompareData, fetchCompareGraphData } from "./thunk";
import { setSelectedOrder } from "src/slices/Filters/reducer";

// Types

export type CompareDataBy = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type CompareChartType = 'line' | 'bar';
export type CompareScreen = 'selection' | 'results';

export interface DateRange {
  start: string;  // 'YYYY-MM-DD'
  end: string;
}

export interface CompareWidgetData {
  range1: Record<string, number>;
  range2: Record<string, number>;
  percent_change: Record<string, number | null>;
}

export interface CompareGraphPoint {
  period: string;
  [metricKey: string]: number | string;
}

export interface CompareGraphData {
  range1: CompareGraphPoint[];
  range2: CompareGraphPoint[];
}

/** Per-exchange compare state, keyed by exchange name. */
export interface ExchangeCompareData {
  exchangeName:      string;
  lineItemIds:       string[];
  orderStart:        string | null;  // order effective date (YYYY-MM-DD)
  orderEnd:          string | null;  // order end date (YYYY-MM-DD)
  screen:            CompareScreen;
  dateRange1:        DateRange;
  dateRange2:        DateRange;
  selectedMetrics:   string[];
  selectedMetricKey: string | null;
  dataBy:            CompareDataBy;
  chartType:         CompareChartType;
  widgetData:        CompareWidgetData | null;
  graphData:         CompareGraphData | null;
  loading:           boolean;  // fetchCompareData in flight
  graphLoading:      boolean;  // fetchCompareGraphData in flight
  error:             string | null;
}

export interface CompareState {
  /** Name of the exchange whose panel is currently open. null = no panel open. */
  openExchange: string | null;
  /** Per-exchange compare state — persists across open/close within the same page session. */
  exchanges: Record<string, ExchangeCompareData>;
}

// Helpers


function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}


/** Split order date range in half: DR2 = first half, DR1 = second half (capped at today). */
function computeSplitDefaults(
  orderStart: string,
  orderEnd: string,
): { dr1: DateRange; dr2: DateRange } {
  const [sy, sm, sd] = orderStart.split('-').map(Number);
  const [ey, em, ed] = orderEnd.split('-').map(Number);

  const start = new Date(sy, sm - 1, sd);
  const orderEndDate = new Date(ey, em - 1, ed);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const effectiveEnd = orderEndDate > today ? today : orderEndDate;

  const totalDays = Math.round(
    (effectiveEnd.getTime() - start.getTime()) / 86_400_000,
  );
  const halfDays = Math.max(1, Math.floor(totalDays / 2));

  // midpoint = start + halfDays
  const midpoint = new Date(start);
  midpoint.setDate(midpoint.getDate() + halfDays);

  // DR2 (Previous) = start → midpoint - 1
  const dr2End = new Date(midpoint);
  dr2End.setDate(dr2End.getDate() - 1);

  return {
    dr2: { start: formatLocalDate(start),    end: formatLocalDate(dr2End) },
    dr1: { start: formatLocalDate(midpoint), end: formatLocalDate(effectiveEnd) },
  };
}

function defaultExchangeState(exchangeName: string): ExchangeCompareData {
  return {
    exchangeName,
    lineItemIds:       [],
    orderStart:        null,
    orderEnd:          null,
    screen:            'selection',
    dateRange1:        { start: '', end: '' },
    dateRange2:        { start: '', end: '' },
    selectedMetrics:   [],
    selectedMetricKey: null,
    dataBy:            'week',
    chartType:         'line',
    widgetData:        null,
    graphData:         null,
    loading:           false,
    graphLoading:      false,
    error:             null,
  };
}

// Initial state

const initialState: CompareState = {
  openExchange: null,
  exchanges:    {},
};

// Slice

const compareSlice = createSlice({
  name: 'compare',
  initialState,
  reducers: {
    /** Open panel for an exchange. First open sets split defaults; re-opens preserve state. */
    openPanel(
      state,
      action: PayloadAction<{
        exchangeGroupName: string;
        lineItemIds:       string[];
        orderStart?:       string;
        orderEnd?:         string;
      }>
    ) {
      const { exchangeGroupName: exchangeName, lineItemIds, orderStart, orderEnd } = action.payload;

      if (!state.exchanges[exchangeName]) {
        state.exchanges[exchangeName] = defaultExchangeState(exchangeName);
      }

      state.openExchange = exchangeName;

      const ex = state.exchanges[exchangeName];
      ex.lineItemIds = lineItemIds;

      // Always update order bounds so the pickers stay in sync with the selected order.
      if (orderStart) ex.orderStart = orderStart;
      if (orderEnd)   ex.orderEnd   = orderEnd;

      // Set split defaults only on first open (no dates chosen yet).
      // Re-opens preserve whatever the user last selected.
      if (!ex.dateRange1.start && orderStart && orderEnd) {
        const { dr1, dr2 } = computeSplitDefaults(orderStart, orderEnd);
        ex.dateRange1 = dr1;
        ex.dateRange2 = dr2;
      }
    },

    /** Close the panel. Per-exchange state is preserved for when the panel reopens. */
    closePanel(state) {
      state.openExchange = null;
    },

    setScreen(state, action: PayloadAction<{ exchangeName: string; screen: CompareScreen }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.screen = action.payload.screen;
    },

    setDateRange1(state, action: PayloadAction<{ exchangeName: string; range: DateRange }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.dateRange1 = action.payload.range;
    },

    setDateRange2(state, action: PayloadAction<{ exchangeName: string; range: DateRange }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.dateRange2 = action.payload.range;
    },

    setSelectedMetrics(state, action: PayloadAction<{ exchangeName: string; metrics: string[] }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.selectedMetrics = action.payload.metrics;
    },

    setSelectedMetricKey(state, action: PayloadAction<{ exchangeName: string; key: string }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.selectedMetricKey = action.payload.key;
    },

    setDataBy(state, action: PayloadAction<{ exchangeName: string; dataBy: CompareDataBy }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.dataBy = action.payload.dataBy;
    },

    setChartType(state, action: PayloadAction<{ exchangeName: string; chartType: CompareChartType }>) {
      const ex = state.exchanges[action.payload.exchangeName];
      if (ex) ex.chartType = action.payload.chartType;
    },

    /** Full reset — dispatched when the user navigates away from the Reports page. */
    resetCompareState() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // Order change — clear compare state for the previous order.
    builder.addCase(setSelectedOrder, (state) => {
      state.openExchange = null;
      state.exchanges    = {};
    });

    // fetchCompareData (widgets + graph)
    builder
      .addCase(fetchCompareData.pending, (state, action) => {
        const ex = state.exchanges[action.meta.arg.exchangeName];
        if (ex) { ex.loading = true; ex.error = null; }
      })
      .addCase(fetchCompareData.fulfilled, (state, action) => {
        const ex = state.exchanges[action.payload.exchangeName];
        if (!ex) return;
        ex.loading    = false;
        ex.widgetData = action.payload.widgets ?? null;
        ex.graphData  = action.payload.graph   ?? null;
        ex.screen     = 'results';

        const keyIsValid = ex.selectedMetricKey !== null
          && ex.selectedMetrics.includes(ex.selectedMetricKey)
          && (
            (ex.widgetData?.range1[ex.selectedMetricKey] ?? 0) !== 0
            || (ex.widgetData?.range2[ex.selectedMetricKey] ?? 0) !== 0
          );
        if (!keyIsValid && ex.selectedMetrics.length > 0) {
          const firstWithData = ex.selectedMetrics.find(k =>
            (ex.widgetData?.range1[k] ?? 0) !== 0 || (ex.widgetData?.range2[k] ?? 0) !== 0
          );
          ex.selectedMetricKey = firstWithData ?? ex.selectedMetrics[0];
        }
      })
      .addCase(fetchCompareData.rejected, (state, action) => {
        const ex = state.exchanges[action.meta.arg.exchangeName];
        if (ex) {
          ex.loading = false;
          ex.error   = (action.payload as string) ?? 'Failed to fetch compare data';
        }
      });

    // fetchCompareGraphData (graph-only refresh on Data By change)
    builder
      .addCase(fetchCompareGraphData.pending, (state, action) => {
        const ex = state.exchanges[action.meta.arg.exchangeName];
        if (ex) { ex.graphLoading = true; ex.error = null; }
      })
      .addCase(fetchCompareGraphData.fulfilled, (state, action) => {
        const ex = state.exchanges[action.payload.exchangeName];
        if (!ex) return;
        ex.graphLoading = false;
        if (action.payload.graph) ex.graphData = action.payload.graph;
      })
      .addCase(fetchCompareGraphData.rejected, (state, action) => {
        const ex = state.exchanges[action.meta.arg.exchangeName];
        if (ex) {
          ex.graphLoading = false;
          ex.error = (action.payload as string) ?? 'Failed to fetch graph data';
        }
      });
  },
});

export const {
  openPanel,
  closePanel,
  setScreen,
  setDateRange1,
  setDateRange2,
  setSelectedMetrics,
  setSelectedMetricKey,
  setDataBy,
  setChartType,
  resetCompareState,
} = compareSlice.actions;

export default compareSlice.reducer;
